import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EtatService } from '../../../Services/etat';
import { ParcService, ReponseChiffresUtiles } from '../../../Services/parc';
import { Installation as InstallationService } from '../../../Services/installation';
import {
  EnergieUnitePipe,
  PuissanceUnitePipe,
  DeviseUnitePipe,
  Co2UnitePipe,
} from '../../../Pipes/unite.pipe';

type LigneDetail = {
  idInstallation: number;
  nom: string;
  totalProduction: number;
  totalRevenus: number;
  lastValueRefPuissance: number | null;
};

type ColonneDetail = 'nom' | 'totalProduction' | 'totalRevenus' | 'lastValueRefPuissance';

@Component({
  selector: 'app-debug-chiffres-utiles',
  imports: [FormsModule, EnergieUnitePipe, PuissanceUnitePipe, DeviseUnitePipe, Co2UnitePipe],
  templateUrl: './debug-chiffres-utiles.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './debug-chiffres-utiles.css',
})
export class DebugChiffresUtiles {
  private etat = inject(EtatService);
  private parcService = inject(ParcService);
  private installationService = inject(InstallationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  parcs = this.etat.parcs;
  parcIdSelectionne = signal<number | null>(this.etat.parcs().find((p) => p.global)?.id ?? null);

  chargement = signal(false);
  erreur = signal<string | null>(null);
  reponse = signal<ReponseChiffresUtiles | null>(null);
  private nomParId = signal<Map<number, string>>(new Map());
  sortEtatDetail = signal<{ colonne: ColonneDetail; direction: 'asc' | 'desc' } | null>(null);

  detailLignes = computed<LigneDetail[]>(() => {
    const noms = this.nomParId();
    return (this.reponse()?.detail ?? []).map((d) => ({
      idInstallation: d.id_installation,
      nom: noms.get(d.id_installation) ?? `#${d.id_installation}`,
      totalProduction: d.total_production,
      totalRevenus: d.total_revenus,
      lastValueRefPuissance: d.last_value_ref_puissance,
    }));
  });

  detailLignesTriees = computed<LigneDetail[]>(() => {
    const lignes = this.detailLignes();
    const sort = this.sortEtatDetail();
    if (!sort) return lignes;
    return [...lignes].sort((a, b) => {
      let cmp: number;
      if (sort.colonne === 'nom') {
        cmp = a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' });
      } else {
        const va = a[sort.colonne] ?? -Infinity;
        const vb = b[sort.colonne] ?? -Infinity;
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      }
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  });

  trierParDetail(colonne: ColonneDetail) {
    const current = this.sortEtatDetail();
    if (!current || current.colonne !== colonne) {
      this.sortEtatDetail.set({ colonne, direction: 'asc' });
    } else if (current.direction === 'asc') {
      this.sortEtatDetail.set({ colonne, direction: 'desc' });
    } else {
      this.sortEtatDetail.set(null);
    }
  }

  iconeSortDetail(colonne: ColonneDetail): string {
    const s = this.sortEtatDetail();
    if (!s || s.colonne !== colonne) return 'unfold_more';
    return s.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  constructor() {
    const p = this.route.snapshot.queryParams;
    const parcId = parseInt(p['parcId'], 10);
    if (!isNaN(parcId)) this.parcIdSelectionne.set(parcId);
  }

  async chargerDonnees() {
    const parcId = this.parcIdSelectionne();
    if (parcId === null || isNaN(parcId)) return;

    this.chargement.set(true);
    this.erreur.set(null);
    this.reponse.set(null);

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { parcId },
      replaceUrl: true,
    });

    try {
      const [res, installations] = await Promise.all([
        this.parcService.getChiffresUtiles(parcId, true),
        this.installationService.listeInstallations(parcId),
      ]);
      this.nomParId.set(new Map((installations ?? []).map((i) => [i.id, i.nom])));
      this.reponse.set(res);
      if (!res) this.erreur.set('Réponse vide ou invalide.');
    } catch (e: any) {
      this.erreur.set(e?.message ?? 'Erreur inconnue');
    } finally {
      this.chargement.set(false);
    }
  }

  get reponseJson(): string {
    const r = this.reponse();
    return r ? JSON.stringify(r, null, 2) : '';
  }

  fmt(v: number | null, decimales = 2): string {
    if (v === null || v === undefined) return '—';
    return v.toLocaleString('fr-FR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }
}
